import {computed, inject, Injectable, signal, Signal, WritableSignal} from '@angular/core';
import {VaultService as BfaVaultService} from '../../bfa-api/services/vault.service';
import {lastValueFrom} from 'rxjs';
import {ListItemMappingService} from '../mapping/list-item-mapping/list-item-mapping.service';
import {ListCacheItem} from '../../types/list/list-cache-item.type';
import {HttpContext} from '@angular/common/http';
import {AUTH_TOKEN_HTTP_CONTEXT} from '../../constants/auth-token-http-context.constant';
import {AuthService} from '../auth/auth.service';
import {AppComponentService} from '../app-component/app-component.service';
import {AppComponentApiIdsEnum} from '../../enums/app-component-api-ids.enum';
import {ListItem} from 'src/app/models/list/list-item.model';

@Injectable({
    providedIn: 'root'
})
export class InProgressService {
    private readonly bfaVaultService = inject(BfaVaultService);
    private readonly listItemMappingService = inject(ListItemMappingService);
    private readonly authService = inject(AuthService);
    private readonly appComponentService = inject(AppComponentService);
    private readonly inProgressList: WritableSignal<ListCacheItem | undefined> = signal(undefined);

    public getInProgressListSignal(vaultId: string): Signal<ListCacheItem | undefined> {
        if (vaultId && this.inProgressList()?.vaultId === vaultId) {
            return this.inProgressList;
        }

        return signal(undefined);
    }

    public getInProgressList(vaultId: string): ListCacheItem | undefined {
        const inProgressList = this.inProgressList();
        if (inProgressList && inProgressList.vaultId === vaultId) {
            return inProgressList;
        }
        return undefined;
    }

    public async fetchInProgressList(vaultId: string): Promise<void> {
        const token = await this.authService.getToken();
        if (!!token) {
            try {
                const inProgressList = await lastValueFrom(this.bfaVaultService.vaultGetInProgressDocumentList({ vaultId }, new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)));
                const inProgressDocuments = inProgressList.items.map(documentListItem => this.listItemMappingService.parseDocumentListItemToInProgressDocumentListItem(documentListItem))
                    .sort(this.sortListItemsByInProgressSinceDateDesc);
                const listCacheItem: ListCacheItem = {
                    vaultId,
                    listItems: inProgressDocuments,
                    permissions: inProgressList.permissions
                };
                this.inProgressList.set(listCacheItem);
            } catch (error) {
                this.inProgressList.set(undefined);
                throw error;
            }
        }
    }

    public getInProgressListItemIds(vaultId: string): Signal<Array<string> | undefined> {
        return computed(() => {
            const inProgressList = this.inProgressList();
            if (inProgressList && vaultId === inProgressList.vaultId) {
                return inProgressList.listItems.map(listItem => listItem.id);
            }
            return undefined;
        });
    }

    public removeListItem(listItemId: string): void {
        this.inProgressList.update(listCacheItem => {
            if (!listCacheItem) {
                return listCacheItem;
            }

            return {
                ...listCacheItem,
                listItems: [...listCacheItem.listItems.filter(listItem => listItem.id !== listItemId)]
            };
        });

        this.appComponentService.getAppComponentApiById(AppComponentApiIdsEnum.IN_PROGRESS_LIST)
            .then((componentApi) => {
                if (componentApi.removeListItem) {
                    componentApi.removeListItem(listItemId);
                }
            });
    }

    private readonly sortListItemsByInProgressSinceDateDesc = (a: ListItem, b: ListItem): number => {
        return a.inProgressSinceDate! > b.inProgressSinceDate! ? -1 : 1;
    };
}
